import * as React from "react";

/**
 * Forma do viewport — a decisão que `useIsMobile` não conseguia tomar.
 *
 * `useIsMobile` media só largura (`< 768px`). Um telefone deitado tem
 * 844px de largura e 390px de altura: passava como desktop e recebia o
 * chrome de desktop — na Loja, quatro caixas de moeda/busca/ordenação
 * mais a barra de abas comiam ~300px dos 390px disponíveis e o catálogo
 * ficava sem altura nenhuma. O aparelho é o mesmo; só girou.
 *
 * Aqui a forma tem dois eixos independentes:
 *
 * - `isShort`   — altura escassa (telefone deitado). É o eixo que o
 *                 Tailwind já expõe como variante `short:`; este hook é
 *                 o mesmo corte para quando a decisão é de estrutura
 *                 (qual árvore renderizar) e não de estilo.
 * - `isNarrow`  — largura escassa (telefone em pé).
 *
 * `isCompact` é a união: "não há espaço para chrome de desktop".
 * Quem só precisa saber isso — a maioria — usa `useIsCompact()`.
 */

const NARROW_MAX = 767;
const SHORT_MAX = 500;

export interface Viewport {
  /** Telefone em pé: largura escassa. */
  isNarrow: boolean;
  /** Telefone deitado: altura escassa. Espelha a variante `short:`. */
  isShort: boolean;
  /** Qualquer um dos dois — sem espaço para chrome de desktop. */
  isCompact: boolean;
}

const QUERY = `(max-width: ${NARROW_MAX}px), (max-height: ${SHORT_MAX}px) and (orientation: landscape)`;
const NARROW_QUERY = `(max-width: ${NARROW_MAX}px)`;
const SHORT_QUERY = `(max-height: ${SHORT_MAX}px) and (orientation: landscape)`;

function read(): Viewport {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return { isNarrow: false, isShort: false, isCompact: false };
  }
  const isNarrow = window.matchMedia(NARROW_QUERY).matches;
  const isShort = window.matchMedia(SHORT_QUERY).matches;
  return { isNarrow, isShort, isCompact: isNarrow || isShort };
}

/**
 * Assina as duas media queries de uma vez.
 *
 * O estado é um objeto novo a cada avaliação, então a comparação campo a
 * campo evita re-render quando a rotação não muda a classificação (girar
 * um tablet, por exemplo, dispara o evento mas não muda nada aqui).
 */
export function useViewport(): Viewport {
  const [viewport, setViewport] = React.useState<Viewport>(read);

  React.useEffect(() => {
    const mql = window.matchMedia(QUERY);
    const sync = () => {
      const next = read();
      setViewport((prev) =>
        prev.isNarrow === next.isNarrow && prev.isShort === next.isShort ? prev : next,
      );
    };
    // A primeira leitura acontece no `useState`, mas em SSR/hidratação ela
    // devolve o padrão — daí o sync imediato.
    sync();
    mql.addEventListener("change", sync);
    // `orientationchange` chega antes de o navegador atualizar as media
    // queries em alguns WebViews do Android; o rAF lê depois do reflow.
    const onOrientation = () => requestAnimationFrame(sync);
    window.addEventListener("orientationchange", onOrientation);
    window.addEventListener("resize", sync);
    return () => {
      mql.removeEventListener("change", sync);
      window.removeEventListener("orientationchange", onOrientation);
      window.removeEventListener("resize", sync);
    };
  }, []);

  return viewport;
}

/** Atalho para o caso comum: "cabe chrome de desktop aqui?" */
export function useIsCompact(): boolean {
  return useViewport().isCompact;
}
